// hooks/useCollections.js
import React, { useState, useCallback, useMemo, useEffect, memo } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';

const COLLECTION_KEY = 'userCollection';
const COLLECTION_COUNT_KEY = 'collectionNumber';

// ============================================
// Icons (lucide, inlined to match the rest of the app)
// ============================================

const FolderPlusIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="lucide lucide-folder-plus"
    aria-hidden="true"
    {...props}
  >
    <path d="M12 10v6" />
    <path d="M9 13h6" />
    <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
  </svg>
);

const FolderCheckIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="lucide lucide-folder-check"
    aria-hidden="true"
    {...props}
  >
    <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
    <path d="m9 13 2 2 4-4" />
  </svg>
);

// ============================================
// Utility Functions
// ============================================

const formatDate = (inputDate) => {
  if (!inputDate) return '';
  if (typeof inputDate === 'string' && /^\d{1,2}-\d{1,2}-\d{4}$/.test(inputDate)) {
    return inputDate;
  }
  try {
    const date = new Date(inputDate);
    if (isNaN(date.getTime())) return '';
    const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    const day = utcDate.getDate();
    const month = utcDate.getMonth() + 1;
    const year = utcDate.getFullYear();
    return `${month}-${day}-${year}`;
  } catch {
    return '';
  }
};

const isStorageAvailable = () => {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

// ============================================
// Collection change notifications
//
// Every FeedItem calls useCollections() independently, so each card owns its
// own hook state. A module-level version counter lets all of them re-check
// their saved status when the collection changes anywhere in the app
// (including the collections page, and other tabs via the storage event).
// ============================================

let collectionVersion = 0;
const collectionListeners = new Set();

const notifyCollectionChange = () => {
  collectionVersion += 1;
  collectionListeners.forEach((listener) => listener(collectionVersion));
};

const readCollection = () => {
  try {
    return JSON.parse(localStorage.getItem(COLLECTION_KEY) || '[]');
  } catch {
    return [];
  }
};

// Parse localStorage once per version instead of once per rendered card.
let cachedCollection = null;
let cachedVersion = -1;

const getCachedCollection = () => {
  if (cachedCollection === null || cachedVersion !== collectionVersion) {
    cachedCollection = readCollection();
    cachedVersion = collectionVersion;
  }
  return cachedCollection;
};

// Saved entries get a generated uuid for `id`, so identity has to come from
// the source fields that survive the save. `detailsLink` is the search path's
// name for the same value FeedItem calls `url`, so both resolve to the same
// key. Title alone is not enough for urlless items: every daily personalized
// report carries the same title, so the date is what separates one from the
// next.
const getItemKey = (entry) => {
  if (!entry) return '';
  if (entry.url) return entry.url;
  if (entry.detailsLink) return entry.detailsLink;
  if (entry.docLink) return entry.docLink;
  if (entry.title) return entry.date ? `${entry.title}|${entry.date}` : entry.title;
  return '';
};

const isKeyInCollection = (key) => {
  if (!key) return false;
  return getCachedCollection().some((saved) => getItemKey(saved) === key);
};

const useCollectionVersion = () => {
  const [version, setVersion] = useState(collectionVersion);

  useEffect(() => {
    collectionListeners.add(setVersion);

    const handleStorage = (event) => {
      if (event.key === COLLECTION_KEY) {
        notifyCollectionChange();
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      collectionListeners.delete(setVersion);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  return version;
};

// ============================================
// Collection Icon
//
// The folder glyph on its own, for the components that already have their own
// button markup (different ids, extra labels, a save handler owned by a
// parent). Drop it in where a <FolderPlus /> used to sit.
// ============================================

export const CollectionIcon = memo(({ item, ...props }) => {
  const version = useCollectionVersion();
  const itemKey = getItemKey(item);

  const isSaved = useMemo(() => {
    void version; // re-evaluate whenever the collection changes
    return isKeyInCollection(itemKey);
  }, [itemKey, version]);

  return isSaved ? <FolderCheckIcon {...props} /> : <FolderPlusIcon {...props} />;
});

CollectionIcon.displayName = 'CollectionIcon';

// Shared style for both modal footer buttons
const collectionButtonStyle = {
  backgroundColor: 'var(--bg-button)',
  color: 'var(--text-primary)',
  border: 'none',
  borderRadius: '20px',
  padding: '8px 20px',
  fontFamily: 'var(--site-font)',
  boxShadow: '2px 2px 12px var(--shadow-color)',
};

// ============================================
// Collection Modal Component
// ============================================

export const CollectionModal = memo(({
  isModalOpen,
  currentItem,
  saveSuccess,
  note,
  setNote,
  handleSave,
  closeModal,
  isStorageAvailable: isStorageAvailableProp
}) => {
  const { t } = useTranslation('common');

  const handleNoteChange = useCallback((e) => {
    setNote(e.target.value);
  }, [setNote]);

  const storageAvailable = typeof isStorageAvailableProp === 'function'
    ? isStorageAvailableProp()
    : isStorageAvailableProp;

  return (
    <Modal
      id="fullBillModal"
      show={isModalOpen}
      size="xl"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      onHide={closeModal}
    >
      <Modal.Header id="billModalHeader" closeButton>
        <h3>{currentItem?.feedName || t('common:addToCollection', 'Add to Collection')}</h3>
      </Modal.Header>
      <Modal.Body id="descriptionModalBody">
        {currentItem && (
          <div className="mb-2">
            <h5>{currentItem.title}</h5>
            <div>{formatDate(currentItem.date)}</div>
          </div>
        )}

        <div id="fullBill">
          <Form id="noteModalForm">
            <Form.Group className="mb-3">
              <Form.Control
                id="noteModalFeedItem"
                placeholder={t('common:addNotePlaceholder', 'Add a note here')}
                type="text"
                as="textarea"
                rows={2}
                value={note}
                onChange={handleNoteChange}
                className="white-placeholder"
              />
            </Form.Group>
          </Form>

          {saveSuccess === false && (
            <div className="alert alert-danger" style={{ margin: '0 20px' }}>
              {t('common:errorSavingToCollection', 'There was an error saving to your collection. Please try again.')}
            </div>
          )}

          {!storageAvailable && (
            <div className="alert alert-warning" style={{ margin: '0 20px' }}>
              {t('common:localStorageUnavailable', 'Local storage is not available in your browser. Collections may not work properly.')}
            </div>
          )}

          <div id="addNoteModalFooter" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '0 4px' }}>
            <Button
              id="collectionCancelButton"
              onClick={closeModal}
              style={collectionButtonStyle}
            >
              {t('common:cancel', 'Cancel')}
            </Button>
            <Button
              id="collectionSaveButton"
              onClick={handleSave}
              disabled={!storageAvailable}
              style={collectionButtonStyle}
            >
              {t('common:saveToCollection', 'Save To Collection')}
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
});

CollectionModal.displayName = 'CollectionModal';

// ============================================
// Collection Button Component
// ============================================

const CollectionButtonComponent = memo(({ item, onClick, className }) => {
  const { t } = useTranslation('common');
  const version = useCollectionVersion();

  const itemKey = getItemKey(item);

  const isSaved = useMemo(() => {
    void version; // re-evaluate whenever the collection changes
    return isKeyInCollection(itemKey);
  }, [itemKey, version]);

  const label = isSaved
    ? t('common:savedToCollection', 'Saved To Collection')
    : t('common:saveToCollection', 'Save To Collection');

  return (
    <Button
      className={`clickable ${isSaved ? 'is-saved' : ''} ${className || ''}`}
      id="addToCollectionButton"
      onClick={() => onClick(item)}
      title={label}
      aria-label={label}
      aria-pressed={isSaved}
    >
      {isSaved ? <FolderCheckIcon /> : <FolderPlusIcon />}
    </Button>
  );
});
CollectionButtonComponent.displayName = 'CollectionButtonComponent';

// ============================================
// useCollections Hook
// ============================================

export function useCollections() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [note, setNote] = useState('');

  const openCollectionModal = useCallback((item) => {
    setCurrentItem(item);
    setIsModalOpen(true);
    setSaveSuccess(null);
    setNote('');
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setNote('');
    setTimeout(() => setCurrentItem(null), 300);
  }, []);

  /**
   * KEY FIX: spread ...currentItem first so ALL custom fields
   * (fullResult, allSources, factText, keyFigure, type, context, tags, source, etc.)
   * are preserved in localStorage — not just the hardcoded subset.
   */
  const handleSave = useCallback(() => {
    if (!currentItem) return false;

    try {
      const itemId = uuidv4();
      const savedAt = new Date().toISOString();

      const collectionString = localStorage.getItem(COLLECTION_KEY) || '[]';
      const collection = JSON.parse(collectionString);

      collection.push({
        ...currentItem,     // preserves ALL fields including custom ones
        id: itemId,         // override: generated id
        note: note || '',   // override: user's note from modal
        savedAt: savedAt,   // override: save timestamp
      });

      localStorage.setItem(COLLECTION_KEY, JSON.stringify(collection));

      // Backward-compat counter
      let collectionCounter = parseInt(localStorage.getItem(COLLECTION_COUNT_KEY) || '0');
      collectionCounter++;
      localStorage.setItem(COLLECTION_COUNT_KEY, collectionCounter.toString());

      notifyCollectionChange();

      setSaveSuccess(true);
      setNote('');
      setTimeout(() => closeModal(), 800);
      return true;
    } catch (error) {
      console.error('Error saving to collection:', error);
      setSaveSuccess(false);
      return false;
    }
  }, [currentItem, note, closeModal]);

  const getCollection = useCallback(() => {
    try {
      const collectionString = localStorage.getItem(COLLECTION_KEY) || '[]';
      return JSON.parse(collectionString);
    } catch {
      return [];
    }
  }, []);

  const removeFromCollection = useCallback((itemId) => {
    try {
      const collection = getCollection();
      const updatedCollection = collection.filter(item => item.id !== itemId);
      localStorage.setItem(COLLECTION_KEY, JSON.stringify(updatedCollection));

      const count = parseInt(localStorage.getItem(COLLECTION_COUNT_KEY) || '0');
      if (count > 0) {
        localStorage.setItem(COLLECTION_COUNT_KEY, (count - 1).toString());
      }

      notifyCollectionChange();
      return true;
    } catch (error) {
      console.error('Error removing from collection:', error);
      return false;
    }
  }, [getCollection]);

  const clearCollection = useCallback(() => {
    try {
      localStorage.removeItem(COLLECTION_KEY);
      localStorage.setItem(COLLECTION_COUNT_KEY, '0');

      notifyCollectionChange();
      return true;
    } catch (error) {
      console.error('Error clearing collection:', error);
      return false;
    }
  }, []);

  const getCollectionCount = useCallback(() => {
    return parseInt(localStorage.getItem(COLLECTION_COUNT_KEY) || '0', 10);
  }, []);

  const isInCollection = useCallback((item) => {
    return isKeyInCollection(getItemKey(item));
  }, []);

  const CollectionButton = useMemo(() => {
    const WrappedButton = ({ item, className }) => (
      <CollectionButtonComponent
        item={item}
        onClick={openCollectionModal}
        className={className}
      />
    );
    WrappedButton.displayName = 'CollectionButtonWrapper';
    return WrappedButton;
  }, [openCollectionModal]);

  return {
    isModalOpen,
    currentItem,
    saveSuccess,
    note,
    setNote,
    openCollectionModal,
    closeModal,
    handleSave,
    getCollection,
    removeFromCollection,
    clearCollection,
    getCollectionCount,
    isInCollection,
    isStorageAvailable,
    CollectionButton,
  };
}

export { CollectionButtonComponent as CollectionButton };

export default useCollections;